import { IconPointFilled } from '@tabler/icons-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { EmptyState } from '../../EmptyState.js';
import { formatDay } from '../../format.js';
import { PageHeader } from '../../PageHeader.js';
import { Panel } from '../../Panel.js';
import { StatusBadge } from '../../StatusBadge.js';
import table from '../materials/Materials.module.css';
import { errorCode } from '../personnel/source.js';
import classes from './Purchasing.module.css';
import { formatAmount, sortItems } from './requisitions.js';
import { RFQ_TONE } from './rfqs.js';
import type { RfqSource } from './source.js';
import { LoadError, SortableTh, type SortState, TablePager, TableSkeleton } from './table.js';
import type { RfqList as RfqListData, RfqStatus, RfqSummary } from './types.js';

const PAGE_SIZE = 10;

type Column =
  | 'rfqNo'
  | 'requisitionNo'
  | 'vessel'
  | 'requestDate'
  | 'closingDate'
  | 'suppliers'
  | 'quotations'
  | 'best'
  | 'status';

const SORT_VALUE: Record<Column, (item: RfqSummary) => string | number | null> = {
  rfqNo: (item) => item.rfqNo,
  requisitionNo: (item) => item.requisitionNo,
  vessel: (item) => item.vesselName,
  requestDate: (item) => item.requestDate,
  closingDate: (item) => item.closingDate,
  suppliers: (item) => item.supplierCount,
  quotations: (item) => item.quotationCount,
  best: (item) => item.best?.landedTotal ?? null,
  status: (item) => item.status,
};
const NUMERIC: ReadonlySet<Column> = new Set(['suppliers', 'quotations']);

type State =
  | { kind: 'loading' }
  | { kind: 'error'; code: string }
  | { kind: 'ready'; list: RfqListData };

export function RfqStatusBadge({ status }: { status: RfqStatus }) {
  const { t } = useTranslation();
  return (
    <StatusBadge tone={RFQ_TONE[status]} icon={<IconPointFilled size={10} />}>
      {t(`purchasing.rfq.statuses.${status}`)}
    </StatusBadge>
  );
}

/**
 * RFQ listesi (ofis; M12b Task 14): onaylı taleplerden açılan teklif istekleri, davetli
 * tedarikçi ve teklif sayısı, en iyi fiyat ve durum. RFQ talebin kartından açılır.
 */
export function RfqList({ source, onOpen }: { source: RfqSource; onOpen: (id: string) => void }) {
  const { t, i18n } = useTranslation();
  const locale = i18n.resolvedLanguage ?? 'en';
  const k = (key: string, options: Record<string, unknown> = {}) =>
    t(`purchasing.rfq.${key}`, options);
  const [state, setState] = useState<State>({ kind: 'loading' });
  const [page, setPage] = useState(1);
  const [sort, setSort] = useState<SortState<Column>>({ key: 'rfqNo', dir: 'desc' });

  const load = useCallback(async () => {
    setState({ kind: 'loading' });
    try {
      setState({ kind: 'ready', list: await source.list() });
    } catch (error) {
      setState({ kind: 'error', code: errorCode(error) });
    }
  }, [source]);
  useEffect(() => {
    void load();
  }, [load]);

  const ready = state.kind === 'ready' ? state.list : null;
  const items = useMemo(
    () => (ready ? sortItems(ready.items, sort.dir, SORT_VALUE[sort.key]) : []),
    [ready, sort],
  );
  const pages = Math.max(1, Math.ceil(items.length / PAGE_SIZE));
  const current = Math.min(page, pages);
  const visible = items.slice((current - 1) * PAGE_SIZE, current * PAGE_SIZE);

  return (
    <div className={classes.screen}>
      <PageHeader title={k('title')} description={k('description')} />
      <Panel flush aria-label={k('table')}>
        {state.kind === 'error' ? (
          <LoadError title={k('error')} code={state.code} onRetry={() => void load()} />
        ) : state.kind === 'loading' ? (
          <TableSkeleton columns={9} label={k('table')} />
        ) : items.length === 0 ? (
          <EmptyState title={k('emptyTitle')} description={k('emptyText')} />
        ) : (
          <>
            <section
              className={table.tableWrap}
              aria-label={k('table')}
              // biome-ignore lint/a11y/noNoninteractiveTabindex: kaydırılabilir bölge (WCAG 2.1.1)
              tabIndex={0}
            >
              <table className={`${table.table} ${classes.table}`}>
                <thead>
                  <tr>
                    {(Object.keys(SORT_VALUE) as Column[]).map((column) => (
                      <SortableTh
                        key={column}
                        column={column}
                        label={k(`columns.${column}`)}
                        sort={sort}
                        onSort={setSort}
                        numeric={NUMERIC.has(column)}
                        {...(column === 'rfqNo' ? { className: classes.key } : {})}
                      />
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {visible.map((item) => (
                    <tr key={item.id}>
                      <td className={classes.key}>
                        <a
                          className={classes.link}
                          href={`?id=${item.id}`}
                          aria-label={k('open', { no: item.rfqNo })}
                          onClick={(event) => {
                            event.preventDefault();
                            onOpen(item.id);
                          }}
                        >
                          {item.rfqNo}
                        </a>
                      </td>
                      <td>{item.requisitionNo}</td>
                      <td>{item.vesselName}</td>
                      <td>{formatDay(item.requestDate)}</td>
                      <td>{formatDay(item.closingDate)}</td>
                      <td data-numeric>{item.supplierCount}</td>
                      <td data-numeric>{item.quotationCount}</td>
                      <td className={classes.breakable}>
                        {item.best ? (
                          <>
                            <span className={classes.nowrap}>{`${item.best.supplierName} ·`}</span>{' '}
                            <span className={classes.nowrap}>
                              {`${formatAmount(item.best.landedTotal, locale)} ${item.currency}`}
                            </span>
                          </>
                        ) : (
                          '—'
                        )}
                      </td>
                      <td>
                        <RfqStatusBadge status={item.status} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </section>
            <TablePager
              page={current}
              pages={pages}
              summary={k('showing', {
                from: (current - 1) * PAGE_SIZE + 1,
                to: Math.min(current * PAGE_SIZE, items.length),
                total: items.length,
              })}
              label={k('pagination')}
              onPage={setPage}
            />
          </>
        )}
      </Panel>
    </div>
  );
}
